import React from "react";

import { checkAdminAccess, goTo, useAdminUserContext, useLocation, useNavContext, useNavigate } from "utils";

import { Divider, Icon, Layout, Menu } from "components";

export const AdminNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNavbar } = useNavContext();
  const { user } = useAdminUserContext();

  const submenuItems = [
    {
      submenu: "content",
      items: [
        {
          name: "Articles",
          route: "content/articles",
        },
        {
          name: "Courses",
          route: "content/courses",
        },
        {
          name: "News",
          route: "content/news",
        },
        {
          name: "Popups",
          route: "content/popups",
        },
        {
          name: "Suggestions",
          route: "content/suggestions",
        },
      ],
    },
    {
      submenu: "farms",
      items: [
        {
          name: "Farms",
          route: "farms/farms",
        },
        {
          name: "Fields",
          route: "farms/fields",
        },
        {
          name: "Shapefiles",
          route: "farms/shapefiles",
        },
      ],
    },
    {
      submenu: "carbonPrograms",
      items: [
        {
          name: "Carbon Programs",
          route: "carbon-programs/overview",
        },
        {
          name: "Carbon Program Participation",
          route: "carbon-programs/participation",
        },
        {
          name: "Carbon Program Dates",
          route: "carbon-programs/dates",
        },
      ],
    },
    {
      submenu: "payments",
      items: [
        {
          name: "Pending payments",
          route: "payments/pending",
        },
        {
          name: "Past payments",
          route: "payments/past",
        },
        {
          name: "Referral payments",
          route: "payments/referral",
        },
        {
          name: "Vesting",
          route: "payments/vesting",
        },
      ],
    },
    {
      submenu: "partners",
      items: [
        {
          name: "Partners",
          route: "partners/partners",
        },
        {
          name: "Method credits",
          route: "partners/method-credits",
        },
        {
          name: "Module credits",
          route: "partners/module-credits",
        },
        {
          name: "Predictions",
          route: "partners/predictions",
        },
      ],
    },
    {
      submenu: "programs",
      items: [
        {
          name: "EU funding",
          route: "programs/funding",
        },
        {
          name: "Methods",
          route: "programs/methods",
        },
        {
          name: "Modules",
          route: "programs/modules",
        },
        {
          name: "Crop Explorer",
          route: "programs/crop-explorer",
        },
        {
          name: "Digital Agronomist Crop Practices",
          route: "programs/digital-agronomist-crop-practices",
        },
        {
          name: "Crop Mix Composer Commercial Mixes",
          route: "programs/crop-mix-composer-commercial-mixes",
        },
      ],
    },
    {
      submenu: "redzones",
      items: [
        {
          name: "Exceptions",
          route: "redzones/exceptions",
        },
        {
          name: "Shapefile upload",
          route: "redzones/shapefile-uploads",
        },
      ],
    },
    {
      submenu: "users",
      items: [
        {
          name: "Email tracking",
          route: "users/tracking",
        },
        {
          name: "Users",
          route: "users/users",
        },
        {
          name: "User notifications",
          route: "users/notifications",
        },
      ],
    },
    {
      submenu: "verifications",
      items: [
        {
          name: "Farms",
          route: "verifications/farms",
          dataTestId: "admin-nav-farms",
        },
        {
          name: "Tasks",
          route: "verifications/tasks",
          dataTestId: "admin-nav-tasks",
        },
        {
          name: "CSV upload",
          route: "verifications/csv-upload",
          dataTestId: "admin-nav-csv-upload",
        },
        {
          name: "Quick field access",
          route: "verifications/quick-field-access",
          dataTestId: "admin-nav-quick-field-access",
        },
        {
          name: "NDVI Scores",
          route: "verifications/ndvi-scores",
          dataTestId: "admin-nav-ndvi-scores",
        },
      ],
    },
    {
      submenu: "experiments",
      items: [
        {
          name: "General Experiments",
          route: "soilplus",
        },
        {
          name: "SoilAI Experiments",
          route: "soilai",
        },
      ],
    },
    {
      submenu: "admin",
      items: [
        {
          name: "Admin settings",
          route: "settings/settings",
        },
        {
          name: "Verification recalculation",
          route: "settings/verification-recalculation",
        },
        {
          name: "Calculations",
          route: "settings/calculations",
        },
        {
          name: "Emission Factors",
          route: "settings/emission-factors",
        },
        {
          name: "Admin users",
          route: "settings/users",
        },
        {
          name: "Platform Terms",
          route: "settings/platform-terms",
        },
      ],
    },
  ];

  const getMenuItems = (submenu) => {
    const menu = [];
    submenuItems.forEach((item) => {
      if (item.submenu === submenu) {
        item.items.forEach((list) => {
          if (user && checkAdminAccess(user.role, list.route)) {
            menu.push({
              label: list.name,
              key: `${list.route}`,
              onClick: (e) => goTo(e, navigate, `/${list.route}`),
            });
          } else {
            menu.push({
              label: list.name,
              key: `${list.route}`,
              disabled: true,
            });
          }
        });
      }
    });

    return menu;
  };

  const menuItems = [
    {
      icon: <Icon iconName="tabler/home" />,
      onClick: (e) => goTo(e, navigate, "/dashboard"),
      key: "dashboard",
    },
    {
      icon: <Icon iconName="tabler/file-text" />,
      key: "content",
      children: getMenuItems("content"),
    },
    {
      icon: <Icon iconName="tabler/map-2" />,
      key: "farms",
      children: getMenuItems("farms"),
    },
    {
      icon: <Icon iconName="tabler/leaf" />,
      key: "carbonPrograms",
      children: getMenuItems("carbonPrograms"),
    },
    {
      icon: <Icon iconName="tabler/coin" />,
      key: "payments",
      children: getMenuItems("payments"),
    },
    {
      icon: <Icon iconName="tabler/affiliate" />,
      key: "partners",
      children: getMenuItems("partners"),
    },
    {
      icon: <Icon iconName="tabler/layers-subtract" />,
      key: "programs",
      children: getMenuItems("programs"),
    },
    {
      icon: <Icon iconName="klim/bottle-strikethrough" />,
      key: "redzones",
      children: getMenuItems("redzones"),
    },
    {
      icon: <Icon iconName="tabler/users" />,
      key: "users",
      children: getMenuItems("users"),
    },
    {
      icon: <Icon dataTestId="admin-nav-verifications" iconName="tabler/eye-check" />,
      key: "verifications",
      children: getMenuItems("verifications"),
    },
    {
      label: <Divider className="margin-0 margin-top-100 margin-bottom-100" />,
      key: "divider",
    },
    {
      label: <Icon iconName="tabler/flask" />,
      key: "experiments",
      children: getMenuItems("experiments"),
    },
    {
      label: <Icon iconName="tabler/settings" />,
      key: "admin",
      children: getMenuItems("admin"),
    },
  ];

  const { Sider } = Layout;

  return showNavbar ? (
    <Sider className="admin-sidebar">
      <Menu mode="vertical" selectedKeys={[location.pathname.split("/")[1]]} items={menuItems} />
    </Sider>
  ) : null;
};

export default AdminNav;
